// The message box. Enter sends, Shift+Enter makes a new line, drafts are kept per channel.
// Attach (button, paste, or drop on the chat), type ":" for emoji, "#" for a channel or "@" for a person, pick a GIF,
// and reply to a message. "/" opens the command menu (every installed app's commands, from one registry); a typed
// `/app command args` runs the command instead of sending a message. "+" opens the + menu: upload a file, or pick any
// app's command from the same registry (PlusMenu).
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { applyEmoticons, BLOOM_ID, BLOOM_NAME, commandRegistry, MAX_MESSAGE_LENGTH, encodeCustomEmoji, encodeMentions, EMOTICONS, EVERYONE, gifMenuShows, matchCommands, parseCommand, parseGifCommand, type AppManifest, type Attachment, type CommandEntry, type CustomEmoji, type MentionMember, type ParsedCommand, type Role } from "@bloom/shared";
import { textToSend } from "../stripTracking.ts";
import { REFUSAL_TOAST_MS } from "../useRefusalToast.ts";
import { pasteQuoted } from "../quotePaste.ts";
import { onShareChange, takeShareFor } from "../share.ts";
import { CustomEmojiView, searchCustom, useServerEmoji } from "../customEmoji.tsx";
import { openPicker, PlusMenu } from "./PlusMenu.tsx";
import { byShortcode, emojiReady, frequentEmoji, loadEmoji, noteEmojiUse, searchEmoji, type Emoji } from "../emoji.ts";
import { formatBytes, isImage, isVideo, uploadFile, type FileAttachment } from "../upload.ts";
import type { GifAttachment } from "../gifs.ts";
import { MediaPicker, MEDIA_TOGGLE } from "./MediaPicker.tsx";
import { isTouch } from "../mobile.ts";
import { Avatar } from "./ui.tsx";
import { CloseButton, CloseIcon, RoundButton } from "./icons.tsx";
import { ListGlyph, ListPicker, matchLists, meansPins, namedLists, noteListUse, PinIcon } from "./Lists.tsx";
import { PINS, customEmojiId, emojiToken, emojiUrl, type ListInfo } from "@bloom/shared";
import { EyeOffIcon } from "./Attachments.tsx";
import { formatShortcut, isFormatting } from "../formatKeys.ts";
import { FormatLayer } from "./FormatLayer.tsx";
import { FormatBar } from "./FormatBar.tsx";
import { decodeChannelRefs, decodeMentions, markdownToPlain, messageLinkPath, parseMessageLink, SPOILER_MASK } from "@bloom/shared";
import { expandPastedLinks, messageLinkLabel, PASTED_LINK_MARK, type MessageLinkHost, type PastedLink } from "./MessageLinks.ts";

const drafts = new Map<string, string>();
/** Message links pasted into each stream's draft (shown by their labels; see PastedLink). */
const linkDrafts = new Map<string, PastedLink[]>();
/** Lists picked for the message being written ("+Recipes", the list button), per stream. PINS for the pin. */
const listPicks = new Map<string, string[]>();
/** People picked from the "@" menu, per stream, so "@Maya" is sent as the Maya that was picked. */
const picks = new Map<string, MentionMember[]>();

interface Upload {
  id: string;
  file: File;
  preview?: string;
  progress: number;
  attachment?: FileAttachment;
  error?: string;
  cancel: () => void;
  /** Marked private in the tray: sent blurred, for each person to choose to look (pictures and videos only). */
  private?: boolean;
  /** Sent while still uploading: the queued message (Queued.id) it goes out with once every file is up. */
  queue?: string;
}

/**
 * A message sent while its files were still uploading. The box is free at once (for other messages, or another
 * channel); this goes out, to the conversation it was written in, when the last file is up.
 */
interface Queued {
  id: string;
  /** The text as typed, shown in the strip (and put back in the box if a file fails). */
  text: string;
  deliver: (attachments: Attachment[]) => void;
}

/** Pictures bigger than this get no preview in the tray (decoding them for a thumbnail would be most of the work). */
const PREVIEW_MAX = 20 * 1024 * 1024;

export interface ComposerHandle {
  addFiles: (files: FileList | File[]) => void;
  focus: () => void;
  /** Puts `text` in the box (after what's there, on a new line), focused with the caret at the end. Nothing is sent. */
  insertText: (text: string) => void;
}

interface Props {
  serverId: string;
  /** Stream to send to: a channel id or a thread stream. Drafts are kept per stream. */
  channelId: string;
  placeholder: string;
  /** `lists`: list ids (or PINS) the message goes in as it's posted. */
  onSend: (text: string, attachments: Attachment[], lists: string[]) => void;
  onTyping: () => void;
  replyingTo?: { name: string; excerpt: string } | null;
  onCancelReply?: () => void;
  /** Stable anonymous id for GIF search relevance. */
  customerId: string;
  /** ↑ in an empty box: edit my last message. Returns false if there's nothing to edit. */
  onEditLast?: () => boolean;
  /** Channels for "#" autocomplete, in sidebar order. `group`: the category name. */
  channels?: ChannelOption[];
  /** Members for "@" autocomplete: people online first, then the rest. */
  people?: PersonOption[];
  /** Offer "@everyone" (text channels and threads, not DMs). */
  everyone?: boolean;
  /** Installed apps: their commands fill the "/" menu. */
  apps?: AppManifest[];
  /** A typed or picked command (`/fr add …`). `form`: open its form even if the inputs are all there (Tab). */
  onCommand?: (cmd: ParsedCommand, opts: { form?: boolean }) => void;
  /** A bare app (`/fr`): open its full view. */
  onOpenApp?: (slug: string) => void;
  /** "Bloom a new app" and "Make a new bot", the "/" menu's last items: start a blooming thread (with what was typed after "/"). */
  onBloom?: (prompt: string, bot: boolean) => void;
  /** My role here: menus only offer commands I can run. */
  role?: Role;
  /** Lists I can see: "+" and the list button pick from these (omitted: no lists here). */
  lists?: ListInfo[];
  /** This conversation is a DM (no shared lists). */
  inDm?: boolean;
  /**
   * Message links: one pasted into the box shows as "🔗 Maya in #general" with a preview above the box, and goes out
   * as the link. Omitted: links paste as typed.
   */
  links?: MessageLinkHost;
  /** Makes a list from the picker; resolves with it, or an error to show. */
  onCreateList?: (name: string, visibility: "server" | "private") => Promise<{ ok: true; list: ListInfo } | { ok: false; message: string }>;
}

export interface PersonOption {
  id: string;
  name: string;
  online: boolean;
  /** A bot (docs/bots.md): its avatar and a BOT tag. */
  bot?: boolean;
}

export interface ChannelOption {
  id: string;
  name: string;
  group?: string;
}

/** "#" or "#query" just before the caret, after a space or at the start (so "C#" or URL fragments don't trigger it). */
const HASH_QUERY = /(^|\s)#([a-z0-9-]{0,40})$/i;

/** "@" or "@query" just before the caret, after a space or at the start (so emails don't trigger it). Names can have
 *  spaces, so the query can too; the menu closes once nothing matches. */
const AT_QUERY = /(^|\s)@([^\s@][^@\n]{0,31}|)$/;

/** "/" or "/query" (the app, then maybe a command) at the very start of the box. */
const SLASH_QUERY = /^\/([a-z0-9-]*(?: [a-z0-9-]*)?)$/i;

/** An app whose name or alias is "gif": the built-in /gif wins over it (#632). */
const answersToGif = (a: AppManifest) => a.slug === "gif" || !!a.aliases?.includes("gif");

/** Commands used lately on this device, newest first (they rank first in the menu). */
const RECENT_KEY = "bloom:recent-commands";
function recentCommands(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}
function noteCommand(label: string) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([label, ...recentCommands().filter((l) => l !== label)].slice(0, 20)));
  } catch {
    // private mode: no ranking by recent use
  }
}

/** One row of the "/" menu: a command (or bare app), or the "Ask Bloom" fallback at the end. */
type CommandItem =
  | { kind: "command"; entry: CommandEntry }
  /** The built-in "/gif" (#632). `shadows`: the name of an app that also answers to /gif, which loses to it. */
  | { kind: "gif"; shadows?: string }
  | { kind: "ask"; query: string } | { kind: "bloom"; query: string; bot: boolean };

type Suggest =
  | { kind: "command"; items: CommandItem[]; index: number; start: number; query: string }
  /** The server's own emoji come first. */
  | { kind: "emoji"; items: (Emoji | CustomEmoji)[]; index: number; start: number; query: string }
  | { kind: "channel"; items: ChannelOption[]; index: number; start: number; query: string }
  | { kind: "person"; items: PersonOption[]; index: number; start: number; query: string }
  | { kind: "list"; items: ListOption[]; index: number; start: number; query: string };

/** A "+" menu entry: the pin, or a named list. */
type ListOption = { id: string; label: string; list?: ListInfo };

/** "+" or "+query" just before the caret, after a space or at the start ("1+1" and "C++" don't trigger it). Names
 *  can have spaces; the menu closes once nothing matches. */
const PLUS_QUERY = /(^|\s)\+([^\s+][^+\n]{0,39}|)$/;

/** @Bloom heads the "@" menu everywhere (channels, threads and DMs): asking it is what the menu is most for. */
const BLOOM_OPTION: PersonOption = { id: BLOOM_ID, name: BLOOM_NAME, online: true };

/** Names starting with the query first, then names with a word starting with it; online people first within each. */
export function matchPeople(people: PersonOption[], query: string, everyone: boolean): PersonOption[] {
  const q = query.toLowerCase();
  const all = [BLOOM_OPTION, ...people, ...(everyone ? [{ id: EVERYONE, name: EVERYONE, online: false }] : [])];
  const starts = all.filter((p) => p.name.toLowerCase().startsWith(q));
  const words = q ? all.filter((p) => !starts.includes(p) && p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q))) : [];
  return [...starts, ...words].slice(0, 25);
}

/** Channels whose name starts with the query first, then ones that contain it; sidebar order within each. */
export function matchChannels(channels: ChannelOption[], query: string): ChannelOption[] {
  const q = query.toLowerCase();
  if (!q) return channels;
  return [...channels.filter((c) => c.name.startsWith(q)), ...channels.filter((c) => !c.name.startsWith(q) && c.name.includes(q))];
}

/** ":" or ":query" just before the caret, after a space or at the start (so "10:30" or URLs don't trigger it). */
const COLON_QUERY = /(^|\s):([a-z0-9_+\-]{0,30})$/i;

export const Composer = forwardRef<ComposerHandle, Props>(function Composer(props, ref) {
  const { serverId, channelId, placeholder, onSend, onTyping, replyingTo, onCancelReply, customerId, onEditLast, channels = [], people = [], everyone = true, apps = [], onCommand, onOpenApp, onBloom, role, lists, inDm = false, onCreateList, links } = props;
  const [text, setText] = useState(drafts.get(channelId) ?? "");
  const [pasted, setPastedState] = useState<PastedLink[]>(linkDrafts.get(channelId) ?? []);
  const setPasted = (list: PastedLink[]) => {
    if (list.length) linkDrafts.set(channelId, list);
    else linkDrafts.delete(channelId);
    setPastedState(list);
  };
  // The pasted links whose labels are still in the box (an edited label is plain text again).
  const pastedShown = pasted.filter((l) => text.includes(l.label));
  const [chosen, setChosenState] = useState<string[]>(listPicks.get(channelId) ?? []);
  const setChosen = (ids: string[]) => {
    if (ids.length) listPicks.set(channelId, ids);
    else listPicks.delete(channelId);
    setChosenState(ids);
  };
  const [listPanel, setListPanel] = useState<DOMRect | null | false>(false);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [queued, setQueued] = useState<Queued[]>([]);
  /** Why the last send didn't go: a message over the length limit stays in the box (a new object shows it again). */
  const [refusal, setRefusal] = useState<{ text: string } | null>(null);
  useEffect(() => {
    if (!refusal) return;
    const t = setTimeout(() => setRefusal(null), REFUSAL_TOAST_MS);
    return () => clearTimeout(t);
  }, [refusal]);
  const delivered = useRef(new Set<string>());
  const [suggest, setSuggest] = useState<Suggest | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(blurTimer.current), []);
  const suggestList = useRef<HTMLDivElement>(null);
  const [panel, setPanel] = useState<"emoji" | "gif" | "sticker" | "plus" | null>(null);
  const mediaOpen = panel === "emoji" || panel === "gif" || panel === "sticker";
  // "/gif cats" (#632): the GIF picker opens searched for "cats" and the box is empty meanwhile. `held` is the text it
  // took out; it comes back if the picker closes without a GIF or sticker sent.
  const [gifQuery, setGifQuery] = useState("");
  const held = useRef<{ channel: string; text: string } | null>(null);
  const plusBtn = useRef<HTMLButtonElement>(null);
  /** The + button opens its menu when it has more than uploading to offer. */
  const plusMenu = (apps.length > 0 && !!onCommand) || !!lists;
  // Closing the emoji / GIF / sticker panel puts the cursor back in the message box (not on phones: no keyboard pop).
  const closeMedia = () => {
    setPanel(null);
    if (!isTouch()) area.current?.focus({ preventScroll: true });
  };
  const custom = useServerEmoji();
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setText(drafts.get(channelId) ?? "");
    setPastedState(linkDrafts.get(channelId) ?? []);
    setChosenState(listPicks.get(channelId) ?? []);
    setListPanel(false);
    // Not on phones: opening a channel shouldn't throw up the keyboard over what you came to read.
    // Not from a dialog that opened since the pick (⌘K right after Enter in the quick switcher): the channel
    // finishes loading under it, and the focus must stay in its box or Esc and typing go to this one.
    const held = document.activeElement?.closest(".overlay, [role=dialog]");
    if (!isTouch() && (!held || held.contains(area.current))) area.current?.focus();
  }, [channelId]);

  useEffect(() => {
    if (replyingTo) area.current?.focus();
  }, [replyingTo]);

  // The picker closed: "/gif …" comes back unless a GIF or sticker went (those clear `held`). Where I have since typed
  // something else, what I typed stays. In another channel's draft it comes back only if that draft is empty.
  useEffect(() => {
    if (mediaOpen || !held.current) return;
    const { channel, text: was } = held.current;
    held.current = null;
    setGifQuery("");
    if (channel !== channelId) {
      if (!drafts.get(channel)) drafts.set(channel, was);
    } else if (!text) update(was);
  }, [mediaOpen]);

  const fit = () => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };
  useLayoutEffect(fit, [text]);
  // The box can change width without the text changing (phones fold buttons away as you type, rotation): refit.
  useEffect(() => {
    const el = area.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === w) return;
      w = el.clientWidth;
      fit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const addFiles = (files: FileList | File[]) => {
    // From the live list: a share can call this from an effect set up before the latest uploads were added.
    const list = [...files].slice(0, 10 - live.current.filter((u) => !u.queue).length);
    for (const file of list) {
      const id = crypto.randomUUID();
      const preview = isImage(file.type) && file.size <= PREVIEW_MAX ? URL.createObjectURL(file) : undefined;
      const task = uploadFile(serverId, file, (p) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x))));
      setUploads((u) => [...u, { id, file, preview, progress: 0, cancel: task.cancel }]);
      task.done.then(
        (attachment) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: 1, attachment } : x))),
        (e: Error) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: e.message } : x))),
      );
    }
  };

  // Queued messages go out once all their files are up; if one fails, the message comes back to the box.
  useEffect(() => {
    for (const q of queued) {
      if (delivered.current.has(q.id)) continue;
      const mine = uploads.filter((u) => u.queue === q.id);
      if (mine.some((u) => u.error)) {
        delivered.current.add(q.id);
        setUploads((list) => list.map((u) => (u.queue === q.id ? { ...u, queue: undefined } : u)));
        setQueued((list) => list.filter((x) => x.id !== q.id));
        if (q.text) setText((t) => (t.trim() ? `${q.text}\n${t}` : q.text));
      } else if (mine.every((u) => u.attachment)) {
        delivered.current.add(q.id);
        q.deliver(mine.map((u) => (u.private ? { ...u.attachment!, private: true } : u.attachment!)));
        for (const u of mine) if (u.preview) URL.revokeObjectURL(u.preview);
        setUploads((list) => list.filter((u) => u.queue !== q.id));
        setQueued((list) => list.filter((x) => x.id !== q.id));
      }
    }
  }, [uploads, queued]);

  // Leaving (closing a thread, another server): uploads still going are cancelled, on the server too.
  const live = useRef(uploads);
  live.current = uploads;
  useEffect(() => () => {
    for (const u of live.current) if (!u.attachment && !u.error) u.cancel();
  }, []);

  // A share from another app sent to this conversation (share.ts): its files start uploading and its text joins the
  // box. Nothing is sent. Also when the picked conversation is the one already open (no channel change).
  useEffect(() => {
    const grab = () => {
      const s = takeShareFor(channelId);
      if (!s) return;
      if (s.text) {
        // Into the draft too, so it's still there after a switch to another conversation and back.
        const cur = drafts.get(channelId) ?? "";
        const next = cur.trim() ? `${cur.replace(/\s+$/, "")}\n${s.text}` : s.text;
        setText(next);
        drafts.set(channelId, next);
      }
      if (s.files.length) addFiles(s.files);
      area.current?.focus();
    };
    grab();
    return onShareChange(grab);
  }, [channelId]);

  useImperativeHandle(
    ref,
    () => ({
      addFiles,
      focus: () => area.current?.focus(),
      insertText: (add: string) => {
        const cur = drafts.get(channelId) ?? "";
        const v = cur.trim() ? `${cur.replace(/\s+$/, "")}\n${add}` : add;
        setText(v);
        drafts.set(channelId, v);
        requestAnimationFrame(() => {
          const el = area.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(v.length, v.length);
        });
      },
    }),
    [uploads.length, serverId, channelId],
  );

  // Names the layer behind the text box colours as you type them (@Maya, #general).
  const highlights = useMemo(
    () => ({
      people: people.map((p) => p.name),
      channels: channels.map((c) => c.name),
      emoji: custom.serverId ? custom.emojis.map((e) => ({ name: e.name, src: emojiUrl(custom.serverId!, e.id) })) : [],
    }),
    [people, channels, custom.emojis, custom.serverId],
  );

  const update = (v: string) => {
    setText(v);
    drafts.set(channelId, v);
    if (v.trim()) onTyping();
  };

  // The highlighted suggestion stays in view when arrowing through a long list.
  useEffect(() => {
    suggestList.current?.querySelector(".autocomplete-item.active")?.scrollIntoView({ block: "nearest" });
  }, [suggest?.index, suggest?.kind]);

  // ---- :emoji: and #channel autocomplete ----
  const refreshSuggest = (value: string, caret: number) => {
    const before = value.slice(0, caret);
    const slash = SLASH_QUERY.exec(before);
    if (slash) {
      const q = slash[1]!;
      // The built-in /gif wins over an app that also answers to it: that app's /gif entries are not listed (#632).
      const shadow = apps.find((a) => answersToGif(a));
      const items: CommandItem[] =
        apps.length && onCommand
          ? matchCommands(commandRegistry(apps.filter((a) => !answersToGif(a)), role), q, recentCommands()).slice(0, 12).map((entry) => ({ kind: "command", entry }))
          : [];
      // Right after the slash the apps come first; once the name starts like "gif", the row is on top.
      if (gifMenuShows(q)) items.splice(q.trim() ? 0 : items.length, 0, { kind: "gif", shadows: shadow?.name });
      if (!items.length) return setSuggest(null); // not a command: Enter sends it as a message ("/shrug")
      if (parseGifCommand(`/${q}`) !== null) return setSuggest({ kind: "command", items: [items[0]!], index: 0, start: 0, query: q });
      if (q.trim()) items.push({ kind: "ask", query: q.trim() });
      if (onBloom) items.push({ kind: "bloom", query: q.trim(), bot: false }, { kind: "bloom", query: q.trim(), bot: true });
      return setSuggest({ kind: "command", items, index: 0, start: 0, query: q });
    }
    const plus = lists ? PLUS_QUERY.exec(before) : null;
    if (plus) {
      // A bare "+" offers the pin and every list; typing narrows it.
      const q = plus[2]!;
      const items: ListOption[] = [
        ...(!q || meansPins(q) ? [{ id: PINS, label: "Pin" }] : []),
        ...matchLists(namedLists(lists!, serverId, inDm), q).map((l) => ({ id: l.id, label: l.name, list: l })),
      ].filter((o) => !chosen.includes(o.id));
      if (items.length) return setSuggest({ kind: "list", items: items.slice(0, 12), index: 0, start: caret - q.length - 1, query: q });
    }
    const at = AT_QUERY.exec(before);
    if (at && people.length) {
      // A bare "@" lists everyone in the server; typing narrows it.
      const items = matchPeople(people, at[2]!, everyone);
      if (items.length) return setSuggest({ kind: "person", items, index: 0, start: caret - at[2]!.length - 1, query: at[2]! });
    }
    const h = HASH_QUERY.exec(before);
    if (h && channels.length) {
      // A bare "#" lists every channel; typing narrows it.
      const items = matchChannels(channels, h[2]!);
      return setSuggest(items.length ? { kind: "channel", items, index: 0, start: caret - h[2]!.length - 1, query: h[2]! } : null);
    }
    const m = COLON_QUERY.exec(before);
    if (!m) return setSuggest(null);
    // ":D", ":P" and ":o" are emoticons, not the start of a name: no menu, so Return sends them (#630).
    if (Object.hasOwn(EMOTICONS, m[0].trim())) return setSuggest(null);
    const mine = m[2] ? searchCustom(custom.emojis, m[2], 8) : [];
    const list = emojiReady();
    if (!list) {
      void loadEmoji().then(() => refreshSuggest(area.current?.value ?? "", area.current?.selectionStart ?? 0));
      return;
    }
    // A bare ":" opens the menu with your frequently used emoji (this server's own included); typing narrows it.
    const items = m[2] ? [...mine, ...searchEmoji(list, m[2], 8)].slice(0, 10) : frequentHere(list, 8);
    setSuggest(items.length ? { kind: "emoji", items, index: 0, start: caret - m[2]!.length - 1, query: m[2]! } : null);
  };

  /** My most used emoji as menu entries: Unicode, and this server's emoji that still exist. */
  const frequentHere = (list: Emoji[], n: number): (Emoji | CustomEmoji)[] => {
    const byUnicode = new Map(list.map((e) => [e.unicode, e]));
    const used = frequentEmoji(n * 2, (u) => {
      const id = customEmojiId(u);
      return !id || custom.byId.has(id);
    });
    return used
      .flatMap((u): (Emoji | CustomEmoji)[] => {
        const id = customEmojiId(u);
        const e = id ? custom.byId.get(id) : byUnicode.get(u);
        return e ? [e] : [];
      })
      .slice(0, n);
  };

  const insertAt = (start: number, end: number, insert: string) => {
    const v = text.slice(0, start) + insert + text.slice(end);
    update(v);
    requestAnimationFrame(() => {
      const el = area.current;
      if (!el) return;
      el.focus();
      el.selectionStart = el.selectionEnd = start + insert.length;
    });
  };

  /** "/gif words" (#632): the box empties and the GIF picker opens, searched for the words ("" is its trending view). */
  const openGif = (query: string) => {
    held.current = { channel: channelId, text };
    clear();
    setSuggest(null);
    setGifQuery(query);
    setPanel("gif");
  };

  /** Runs a command picked from the menu: a bare app opens; one without inputs runs; Tab asks for its form. */
  const pickCommand = (item: CommandItem, viaTab: boolean) => {
    if (item.kind === "ask") {
      picks.set(channelId, [...(picks.get(channelId) ?? []), { id: BLOOM_ID, name: BLOOM_NAME }]);
      insertAt(0, text.length, `@${BLOOM_NAME} ${item.query} `);
      return;
    }
    if (item.kind === "bloom") {
      clear();
      onBloom?.(item.query, item.bot);
      return;
    }
    if (item.kind === "gif") {
      // "/gif" or "/gif cats" typed in full opens the picker, as Return would; a start like "/gi" fills in the name.
      const typed = parseGifCommand(text);
      if (typed !== null) openGif(typed);
      else insertAt(0, text.length, "/gif ");
      return;
    }
    const { entry } = item;
    if (!entry.command) {
      noteCommand(entry.label);
      clear();
      onOpenApp?.(entry.app.slug);
      return;
    }
    const takesInput = (entry.command.inputs ?? (entry.command.action ? entry.app.actions[entry.command.action]?.inputs : []) ?? []).length > 0;
    if (viaTab || !takesInput) {
      noteCommand(entry.label);
      clear();
      onCommand?.({ app: entry.app, command: entry.command, args: {} }, { form: viaTab });
      return;
    }
    insertAt(0, text.length, `${entry.label} `);
  };

  const choose = (index: number, viaTab = false) => {
    if (!suggest) return;
    const caret = area.current?.selectionStart ?? text.length;
    if (suggest.kind === "command") {
      pickCommand(suggest.items[index]!, viaTab);
      setSuggest(null);
      return;
    }
    if (suggest.kind === "list") {
      // The list goes on the message as a chip; the "+query" leaves the text.
      const o = suggest.items[index]!;
      noteListUse(serverId, o.id);
      setChosen([...chosen.filter((id) => id !== o.id), o.id]);
      const start = suggest.start > 0 && text[suggest.start - 1] === " " && (caret >= text.length || text[caret] === " ") ? suggest.start - 1 : suggest.start;
      insertAt(start, caret, "");
    } else if (suggest.kind === "channel") {
      insertAt(suggest.start, caret, `#${suggest.items[index]!.name} `);
    } else if (suggest.kind === "person") {
      const p = suggest.items[index]!;
      if (p.id !== EVERYONE) picks.set(channelId, [...(picks.get(channelId) ?? []), { id: p.id, name: p.name }]);
      insertAt(suggest.start, caret, `@${p.name} `);
    } else {
      const e = suggest.items[index]!;
      if ("unicode" in e) {
        noteEmojiUse(e.unicode);
        insertAt(suggest.start, caret, `${e.unicode} `);
      } else {
        noteEmojiUse(emojiToken(e));
        insertAt(suggest.start, caret, `:${e.name}: `); // sent as "<:name:id>"
      }
    }
    setSuggest(null);
  };

  const onChange = (value: string, caret: number) => {
    // A Format item typed its change in: take the text as it is (no ":shortcode:" turns into an emoji).
    if (isFormatting()) {
      update(value);
      return;
    }
    // A finished ":shortcode:" turns into its emoji, like Discord.
    const done = /(^|\s):([a-z0-9_+\-]{2,30}):$/i.exec(value.slice(0, caret));
    const list = emojiReady();
    // A server emoji by that name wins, and stays ":name:" in the box (it's sent as its token).
    const mineDone = done ? custom.emojis.find((x) => x.name.toLowerCase() === done[2]!.toLowerCase()) : undefined;
    // Typing a server emoji's ":name:" in full counts as using it (once, as the closing colon goes in).
    if (mineDone && value.length > text.length) noteEmojiUse(emojiToken(mineDone));
    if (done && list && !mineDone) {
      const hit = byShortcode(list, done[2]!);
      if (hit) {
        const start = caret - done[2]!.length - 2;
        const v = value.slice(0, start) + hit.unicode + value.slice(caret);
        update(v);
        setSuggest(null);
        noteEmojiUse(hit.unicode);
        requestAnimationFrame(() => {
          if (area.current) area.current.selectionStart = area.current.selectionEnd = start + hit.unicode.length;
        });
        return;
      }
    }
    update(value);
    refreshSuggest(value, caret);
  };

  // ---- Sending ----
  // The tray: files not yet sent with a message. Sending while some are still uploading queues the message (Queued).
  const tray = uploads.filter((u) => !u.queue);
  const uploading = tray.some((u) => !u.attachment && !u.error);
  const ready = tray.filter((u) => u.attachment).map((u) => (u.private ? { ...u.attachment!, private: true } : u.attachment!));
  const canSend = text.trim().length > 0 || tray.some((u) => !u.error);

  const clear = () => {
    update("");
    setPasted([]);
    drafts.delete(channelId);
    picks.delete(channelId);
  };

  const send = () => {
    if (!canSend) return;
    // "/gif cats": the GIF picker opens searched for "cats" and nothing is posted, and the built-in wins over an app's /gif (a message with files is a message).
    const gifWords = tray.length ? null : parseGifCommand(text);
    if (gifWords !== null) return openGif(gifWords);
    // "/fr Add themes": a command, not a message (unless it names no installed app, like "/shrug").
    const cmd = onCommand && !tray.length && text.trimStart().startsWith("/") ? parseCommand(text, apps) : null;
    if (cmd) {
      noteCommand(`/${cmd.app.slug}${cmd.command ? ` ${cmd.command.name}` : ""}`);
      clear();
      if (cmd.command) onCommand!(cmd, {});
      else onOpenApp?.(cmd.app.slug);
      return;
    }
    // Pasted message links go back in as their links first, so "#general" in a label isn't taken for a channel.
    const linked = textToSend(expandPastedLinks(text, pasted).text);
    // "@Maya" → "<@id>" now, so my own copy shows the chip at once (the room does the same for anything left).
    const members = people.map((p) => ({ id: p.id, name: p.name }));
    // Last, so a token the encoders made ("<@id>", "<:name:id>") is never taken for an emoticon (#630).
    const body = applyEmoticons(encodeMentions(encodeCustomEmoji(linked, custom.emojis), members, { everyone, picked: picks.get(channelId) }));
    // The room counts the trimmed body as sent, tokens and all, and refuses one over the limit, which no Retry fixes. It
    // stays in the box, never cleared and lost, and the toast says why (as on iOS, `MessageLimit`, and Android, #195).
    const length = body.trim().length;
    if (length > MAX_MESSAGE_LENGTH) return setRefusal({ text: `Messages can be up to ${MAX_MESSAGE_LENGTH} characters (this is ${length}).` });
    setPasted([]);
    picks.delete(channelId);
    const toLists = chosen;
    setChosen([]);
    if (uploading) {
      // Goes out when its files are up, through this render's onSend: to this conversation, with this reply and lists.
      const id = crypto.randomUUID();
      const deliver = onSend;
      setQueued((list) => [...list, { id, text, deliver: (attachments) => deliver(body, attachments, toLists) }]);
      // Files that failed stay in the tray (as when sending without waiting).
      setUploads((list) => list.map((u) => (u.queue || u.error ? u : { ...u, queue: id })));
      onCancelReply?.();
    } else {
      onSend(body, ready, toLists);
      for (const u of tray) if (u.preview) URL.revokeObjectURL(u.preview);
      setUploads((list) => list.filter((u) => u.queue));
    }
    update("");
    drafts.delete(channelId);
  };

  /**
   * A message link pasted on its own: it goes in as "🔗 Maya in #general" (spaced from the words around it, so it
   * goes out as a link) once the room says I can see the message; otherwise it goes in as pasted.
   */
  const pasteLink = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const raw = e.clipboardData.getData("text/plain").trim();
    const ref = links && !/\s/.test(raw) ? parseMessageLink(raw, location.origin) : null;
    if (!links || !ref || ref.serverId !== links.serverId) return;
    e.preventDefault();
    const el = e.currentTarget;
    const [start, end] = [el.selectionStart, el.selectionEnd];
    const put = (insert: string, link?: PastedLink) => {
      const cur = drafts.get(channelId) ?? text;
      const before = cur.slice(0, start);
      const after = cur.slice(end);
      const lead = before && !/\s$/.test(before) ? " " : "";
      const tail = !after || !/^\s/.test(after) ? " " : "";
      const v = before + lead + insert + tail + after;
      update(v);
      if (link) setPasted([...(linkDrafts.get(channelId) ?? []), link]);
      const caret = (before + lead + insert + tail).length;
      requestAnimationFrame(() => area.current?.setSelectionRange(caret, caret));
    };
    void links.resolve(ref.messageId).then((r) => {
      if (!r) return put(raw);
      const author = people.find((p) => p.id === r.message.authorId)?.name ?? (r.message.authorId === BLOOM_ID ? BLOOM_NAME : "Someone");
      const where = channels.find((c) => c.id === r.rootId)?.name;
      const label = PASTED_LINK_MARK + messageLinkLabel(author, where, r.message.channelId !== r.rootId);
      put(label, { label, url: location.origin + messageLinkPath(ref), message: r.message, rootId: r.rootId });
    });
  };
  const unpasteLink = (l: PastedLink) => {
    update(text.replace(l.label, "").replace(/ {2,}/g, " "));
    setPasted(pasted.filter((x) => x !== l));
  };

  const cancelQueued = (q: Queued) => {
    delivered.current.add(q.id);
    for (const u of uploads.filter((x) => x.queue === q.id)) remove(u);
    setQueued((list) => list.filter((x) => x.id !== q.id));
  };

  const remove = (u: Upload) => {
    u.cancel();
    if (u.preview) URL.revokeObjectURL(u.preview);
    setUploads((list) => list.filter((x) => x.id !== u.id));
  };

  return (
    <div className="composer-box">
      {refusal && <div className="toast toast-high" role="status">{refusal.text}</div>}
      {replyingTo && (
        <div className="reply-bar">
          <span>
            Replying to <strong>{replyingTo.name}</strong>
            {replyingTo.excerpt && <span className="muted"> · {replyingTo.excerpt.slice(0, 80)}</span>}
          </span>
          <CloseButton size="sm" onClick={onCancelReply} aria-label="Cancel reply" />
        </div>
      )}
      {pastedShown.map((l, i) => (
        <PastedLinkPreview key={`${l.url}:${i}`} link={l} people={people} channels={channels} onRemove={() => unpasteLink(l)} />
      ))}
      {queued.map((q) => {
        const files = uploads.filter((u) => u.queue === q.id);
        const total = files.reduce((n, u) => n + u.file.size, 0) || 1;
        const pct = Math.round((100 * files.reduce((n, u) => n + u.file.size * (u.attachment ? 1 : u.progress), 0)) / total);
        return (
          <div key={q.id} className="upload-queue" role="status">
            <div className="upload-queue-text">
              <span className="muted">Sending when uploaded · {files.length === 1 ? files[0]!.file.name : `${files.length} files`} · {pct}%</span>
              {q.text && <span className="upload-queue-body">{q.text}</span>}
            </div>
            <div className="upload-bar"><span style={{ width: `${pct}%` }} /></div>
            <CloseButton size="sm" className="upload-queue-cancel" onClick={() => cancelQueued(q)} aria-label="Cancel sending" title="Cancel" />
          </div>
        );
      })}
      {chosen.length > 0 && lists && (
        <div className="list-chips" aria-label="Collections this message goes in">
          <span>Adds to</span>
          {chosen.map((id) => {
            const l = id === PINS ? undefined : lists.find((x) => x.id === id);
            if (id !== PINS && !l) return null;
            return (
              <span key={id} className="list-chip">
                {l ? <ListGlyph list={l} size={12} /> : <PinIcon size={12} />}
                {l ? l.name : "Pinned"}
                <button type="button" className="icon-btn" aria-label={`Don't add to ${l ? l.name : "pins"}`} onClick={() => setChosen(chosen.filter((x) => x !== id))}>
                  <CloseIcon size={10} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {tray.length > 0 && (
        <div className="upload-tray">
          {tray.map((u) => (
            <div key={u.id} className={`upload${u.error ? " failed" : ""}${u.private ? " is-private" : ""}`}>
              {u.preview ? <img src={u.preview} alt="" /> : <div className="upload-file">{u.file.name.split(".").pop()?.toUpperCase().slice(0, 4) || "FILE"}</div>}
              <div className="upload-name" title={u.file.name}>{u.file.name}</div>
              <div className="muted" style={{ fontSize: 11 }}>{u.error ?? (u.attachment ? formatBytes(u.attachment.size) : u.progress >= 1 ? "Finishing…" : `${Math.round(u.progress * 100)}%`)}</div>
              {!u.attachment && !u.error && <div className="upload-bar"><span style={{ width: `${Math.round(u.progress * 100)}%` }} /></div>}
              {(isImage(u.file.type) || isVideo(u.file.type)) && (
                <RoundButton
                  size="sm"
                  tone="media"
                  className="upload-private"
                  aria-pressed={!!u.private}
                  aria-label="Mark as private"
                  title={u.private ? "Private: blurred until each person chooses to look" : "Mark as private"}
                  onClick={() => setUploads((list) => list.map((x) => (x.id === u.id ? { ...x, private: !x.private } : x)))}
                >
                  <EyeOffIcon size={14} />
                </RoundButton>
              )}
              <CloseButton size="sm" tone="media" className="upload-remove" onClick={() => remove(u)} aria-label={`Remove ${u.file.name}`} />
            </div>
          ))}
        </div>
      )}
      {/* has-text: phones fold the extra buttons away while you type, like Discord, so the box gets the width. */}
      <div className={`composer${text.trim() ? " has-text" : ""}${canSend ? " can-send" : ""}`}>
        {suggest?.kind === "command" && (
          <div className="autocomplete autocomplete-scroll command-menu" role="listbox" aria-label="Commands" ref={suggestList}>
            <div className="autocomplete-title">{suggest.query ? `COMMANDS MATCHING /${suggest.query}` : "COMMANDS"}</div>
            {suggest.items.map((item, i) => (
              <button
                key={item.kind === "command" ? item.entry.label : item.kind === "bloom" && item.bot ? "bloom-bot" : item.kind}
                type="button"
                role="option"
                aria-selected={i === suggest.index}
                className={`autocomplete-item${i === suggest.index ? " active" : ""}${item.kind !== "command" && item.kind !== "gif" ? " fallback" : ""}${item.kind === "bloom" ? " bloom-item" : ""}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(i);
                }}
              >
                {item.kind === "gif" ? (
                  <>
                    <span className="glyph app-glyph" aria-hidden="true">🎞️</span>
                    <span className="autocomplete-name">
                      <span className="command-label">/gif</span>
                      <span className="command-desc">Search GIFs{item.shadows ? ` (built in; it replaces the /gif of ${item.shadows})` : ""}</span>
                    </span>
                  </>
                ) : item.kind === "ask" ? (
                  <>
                    <span className="glyph"><Avatar id={BLOOM_ID} name={BLOOM_NAME} size={22} /></span>
                    <span className="autocomplete-name">Ask Bloom about “{item.query}”</span>
                  </>
                ) : item.kind === "bloom" ? (
                  <>
                    <span className="glyph app-glyph" aria-hidden="true">{item.bot ? "🤖" : "🌱"}</span>
                    <span className="autocomplete-name">
                      <span className="command-label">{item.bot ? "Make a new bot" : "Bloom a new app"}</span>
                      <span className="command-desc">{item.bot ? (item.query ? `Build a bot for “${item.query}” with Bloom, in private` : "Your own bot that lives in chat, built with Bloom in private") : item.query ? `Build “${item.query}” with Bloom in a thread` : "Describe an app and build it with Bloom in a thread"}</span>
                    </span>
                  </>
                ) : (
                  <>
                    <span className="glyph app-glyph" aria-hidden="true">{item.entry.app.icon}</span>
                    <span className="autocomplete-name">
                      <span className="command-label">{item.entry.label}</span>
                      <span className="command-desc">{item.entry.description}</span>
                    </span>
                    {apps.length > 1 && <span className="autocomplete-meta">{item.entry.app.name}</span>}
                  </>
                )}
              </button>
            ))}
            <div className="autocomplete-hint">
              <kbd>↵</kbd> pick · <kbd>Tab</kbd> open its form · <kbd>Esc</kbd> close
            </div>
          </div>
        )}
        {suggest?.kind === "emoji" && (
          <div className="autocomplete" role="listbox" aria-label="Emoji matching" ref={suggestList}>
            <div className="autocomplete-title">{suggest.query ? `EMOJI MATCHING :${suggest.query}` : "FREQUENTLY USED · keep typing to search"}</div>
            {suggest.items.map((e, i) => (
              <button
                key={"unicode" in e ? e.unicode : e.id}
                type="button"
                role="option"
                aria-selected={i === suggest.index}
                className={`autocomplete-item${i === suggest.index ? " active" : ""}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(i);
                }}
              >
                {"unicode" in e ? (
                  <>
                    <span className="glyph">{e.unicode}</span>
                    <span>:{e.shortcodes[0] ?? e.label.replace(/\s+/g, "_")}:</span>
                  </>
                ) : (
                  <>
                    <span className="glyph"><CustomEmojiView id={e.id} name={e.name} className="cemoji cemoji-suggest" /></span>
                    <span className="autocomplete-name">:{e.name}:</span>
                    <span className="autocomplete-meta">Server</span>
                  </>
                )}
              </button>
            ))}
          </div>
        )}
        {suggest?.kind === "person" && (
          <div className="autocomplete autocomplete-scroll" role="listbox" aria-label="Members" ref={suggestList}>
            <div className="autocomplete-title">{suggest.query ? `MEMBERS MATCHING @${suggest.query}` : "MEMBERS"}</div>
            {suggest.items.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={i === suggest.index}
                className={`autocomplete-item${i === suggest.index ? " active" : ""}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(i);
                }}
              >
                {p.id === EVERYONE ? <span className="glyph channel-glyph">@</span> : <span className="glyph"><Avatar id={p.id} name={p.name} size={22} presence={p.online && p.id !== BLOOM_ID ? "online" : undefined} /></span>}
                <span className="autocomplete-name">
                  {p.id === EVERYONE ? "@everyone" : p.name}
                  {p.id === BLOOM_ID && <span className="ai-tag">AI</span>}
                  {p.bot && <span className="ai-tag bot-tag">BOT</span>}
                </span>
                {p.id === EVERYONE && <span className="autocomplete-meta">Notify everyone here</span>}
                {p.id === BLOOM_ID && <span className="autocomplete-meta">Ask anything</span>}
              </button>
            ))}
          </div>
        )}
        {suggest?.kind === "list" && (
          <div className="autocomplete autocomplete-scroll" role="listbox" aria-label="Collections" ref={suggestList}>
            <div className="autocomplete-title">{suggest.query ? `COLLECTIONS MATCHING +${suggest.query}` : "PIN OR ADD TO A COLLECTION"}</div>
            {suggest.items.map((o, i) => (
              <button
                key={o.id}
                type="button"
                role="option"
                aria-selected={i === suggest.index}
                className={`autocomplete-item${i === suggest.index ? " active" : ""}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(i);
                }}
              >
                <span className="glyph channel-glyph">{o.list ? <ListGlyph list={o.list} size={17} /> : <PinIcon size={17} />}</span>
                <span className="autocomplete-name">{o.id === PINS ? "Pin to this conversation" : o.label}</span>
                {o.list?.visibility === "private" && <span className="autocomplete-meta">Only you</span>}
              </button>
            ))}
          </div>
        )}
        {suggest?.kind === "channel" && (
          <div className="autocomplete autocomplete-scroll" role="listbox" aria-label="Channels" ref={suggestList}>
            <div className="autocomplete-title">{suggest.query ? `CHANNELS MATCHING #${suggest.query}` : "CHANNELS"}</div>
            {suggest.items.map((c, i) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={i === suggest.index}
                className={`autocomplete-item${i === suggest.index ? " active" : ""}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  choose(i);
                }}
              >
                <span className="glyph channel-glyph">#</span>
                <span className="autocomplete-name">{c.name}</span>
                {c.group && <span className="autocomplete-meta">{c.group}</span>}
              </button>
            ))}
          </div>
        )}
        {(panel === "emoji" || panel === "gif" || panel === "sticker") && (
          <MediaPicker
            tab={panel}
            onTab={(t) => {
              setGifQuery(""); // only the first GIF tab is searched for the "/gif" words
              setPanel(t);
            }}
            gifQuery={gifQuery}
            customerId={customerId}
            onClose={closeMedia}
            onEmoji={(u, mine) => {
              const insert = mine ? `:${mine.name}:` : u;
              const back = held.current?.channel === channelId ? held.current.text : null;
              if (back !== null) {
                // The "/gif" text comes back with the emoji after it.
                held.current = null;
                update(back + (back && !/\s$/.test(back) ? " " : "") + insert);
                setPanel(null);
                return;
              }
              const el = area.current;
              const s = el?.selectionStart ?? text.length;
              const e = el?.selectionEnd ?? text.length;
              insertAt(s, e, insert);
              setPanel(null);
            }}
            onSticker={(st) => {
              held.current = null;
              onSend("", [{ kind: "sticker", id: st.id, name: st.name, format: st.format }], chosen);
              setChosen([]);
              closeMedia();
            }}
            onGif={(gif: GifAttachment) => {
              held.current = null;
              onSend("", [gif], chosen);
              setChosen([]);
              closeMedia();
            }}
          />
        )}
        {panel === "plus" && (
          <PlusMenu
            apps={onCommand ? apps : []}
            role={role}
            onClose={() => setPanel(null)}
            onUpload={(source) => openPicker(fileInput.current, source)}
            onOpenApp={(slug) => onOpenApp?.(slug)}
            onBloom={onBloom ? (kind) => onBloom("", kind === "bot") : undefined}
            // Pin or add to a collection when sent (the bookmark button's job before; "+name" still works too).
            collections={lists ? { picked: chosen.length, open: () => setListPanel(plusBtn.current?.getBoundingClientRect() ?? null) } : undefined}
            onPick={(entry) => {
              // Like the "/" menu with Tab: its form when it takes input; otherwise it runs.
              noteCommand(entry.label);
              onCommand?.({ app: entry.app, command: entry.command, args: {} }, { form: true });
            }}
          />
        )}
        <button
          ref={plusBtn}
          type="button"
          className={`composer-btn plus-btn${panel === "plus" ? " open" : ""}${chosen.length ? " has-lists" : ""}`}
          // With no apps there's nothing to choose between: straight to the file picker.
          // With nothing else to offer (no apps, no collections) it goes straight to the file picker.
          onClick={() => (plusMenu ? setPanel(panel === "plus" ? null : "plus") : openPicker(fileInput.current, "files"))}
          aria-label={plusMenu ? "Upload, pin or use an app" : "Attach files"}
          aria-haspopup="menu"
          aria-expanded={panel === "plus"}
          title={plusMenu ? "Upload, pin or use an app" : "Attach files"}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} />
        <label htmlFor={`composer-${channelId}`} className="sr-only">{placeholder}</label>
        {/* On phones the text and its buttons sit in one rounded field between + and send; on desktop it isn't a box of its own. */}
        <div className="composer-pill">
        <div className="fl-field">
          <FormatLayer area={area} text={text} highlights={highlights} />
          <FormatBar area={area} text={text} setValue={update} />
          <textarea
            id={`composer-${channelId}`}
            ref={area}
            rows={1}
            value={text}
            placeholder={placeholder}
            enterKeyHint={isTouch() ? "enter" : "send"}
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck
            autoComplete="off"
            onChange={(e) => onChange(e.target.value, e.target.selectionStart)}
            onClick={(e) => refreshSuggest(e.currentTarget.value, e.currentTarget.selectionStart)}
            onBlur={() => {
              clearTimeout(blurTimer.current);
              blurTimer.current = setTimeout(() => setSuggest(null), 100);
            }}
            onPaste={(e) => {
              const files = [...e.clipboardData.files];
              if (files.length) {
                e.preventDefault();
                addFiles(files);
                return;
              }
              if (pasteQuoted(e, update)) return;
              pasteLink(e);
            }}
            onKeyDown={(e) => {
              if (suggest) {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  const n = suggest.items.length;
                  setSuggest({ ...suggest, index: (suggest.index + (e.key === "ArrowDown" ? 1 : n - 1)) % n });
                  return;
                }
                if (e.key === "Enter" || e.key === "Tab") {
                  e.preventDefault();
                  choose(suggest.index, e.key === "Tab");
                  return;
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setSuggest(null);
                  return;
                }
              }
              if (formatShortcut(e, update)) return;
              if (e.key === "ArrowUp" && !text && !tray.length && onEditLast?.()) {
                e.preventDefault();
                return;
              }
              if (e.key === "Escape" && replyingTo) {
                onCancelReply?.();
                return;
              }
              // On phones Return makes a new line (like Discord and iMessage); the send button sends.
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !isTouch()) {
                e.preventDefault();
                send();
              }
            }}
            onKeyUp={(e) => {
              if (e.key === "ArrowLeft" || e.key === "ArrowRight") refreshSuggest(e.currentTarget.value, e.currentTarget.selectionStart);
            }}
          />
        </div>
        {listPanel !== false && lists && (
          <ListPicker
            serverId={serverId}
            lists={lists}
            checked={new Set(chosen)}
            pinned={chosen.includes(PINS)}
            inDm={inDm}
            anchor={listPanel}
            title="Add when sent"
            onToggle={(id, on) => setChosen(on ? [...chosen.filter((x) => x !== id), id] : chosen.filter((x) => x !== id))}
            onCreate={async (name, visibility) => {
              const r = await onCreateList?.(name, visibility);
              if (!r) return "Collections aren't available here.";
              if (!r.ok) return r.message;
              noteListUse(serverId, r.list.id);
              setChosen([...chosen, r.list.id]);
              return null;
            }}
            onClose={() => {
              setListPanel(false);
              area.current?.focus();
            }}
          />
        )}
        {/* GIF and Emoji open the same three-tab picker, each on its own tab; pressing the open one closes it. */}
        <button
          type="button"
          className={`composer-btn media-btn gif-btn fold${panel === "gif" ? " open" : ""}`}
          {...{ [MEDIA_TOGGLE]: "" }}
          onClick={() => (panel === "gif" ? closeMedia() : setPanel("gif"))}
          aria-label="GIFs"
          aria-haspopup="dialog"
          aria-expanded={panel === "gif"}
          title="GIFs"
        >
          <svg width="20" height="18" viewBox="0 0 26 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="22" height="14" rx="3" /><path d="M10 10.2a2.3 2.3 0 1 0 0 3.6V12H8.8M13 9.5v5M16.2 14.5v-5h3M16.2 12h2.4" /></svg>
        </button>
        <button
          type="button"
          className={`composer-btn media-btn emoji-btn${panel === "emoji" || panel === "sticker" ? " open" : ""}`}
          {...{ [MEDIA_TOGGLE]: "" }}
          onClick={() => (panel === "emoji" || panel === "sticker" ? closeMedia() : setPanel("emoji"))}
          aria-label="Emoji"
          aria-haspopup="dialog"
          aria-expanded={panel === "emoji" || panel === "sticker"}
          title="Emoji and stickers"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8.5 14.5c1 1.2 2.1 1.8 3.5 1.8s2.5-.6 3.5-1.8M9 9.5h.01M15 9.5h.01" /></svg>
        </button>
        </div>
        <button
          className="btn btn-primary btn-small send-btn"
          onClick={send}
          // Keep the keyboard up after sending from a phone.
          onPointerDown={(e) => e.pointerType !== "mouse" && e.preventDefault()}
          disabled={!canSend}
          aria-label="Send"
        >
          <span className="send-label">Send</span>
          <svg className="send-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
        </button>
      </div>
    </div>
  );
});

/** A pasted message link's preview above the box, before sending: who, where, a line of the text; × takes it out. */
function PastedLinkPreview({ link, people, channels, onRemove }: { link: PastedLink; people: PersonOption[]; channels: ChannelOption[]; onRemove: () => void }) {
  const m = link.message;
  const names = new Map(people.map((p) => [p.id, p.name]));
  const who = names.get(m.authorId) ?? (m.authorId === BLOOM_ID ? BLOOM_NAME : "Someone");
  const channelName = channels.find((c) => c.id === link.rootId)?.name;
  const where = `${channelName ? `#${channelName}` : "Direct message"}${m.channelId !== link.rootId ? " › thread" : ""}`;
  const source = m.body.trim() ? m.body : (m.forward?.body ?? "");
  const byId = new Map(channels.map((c) => [c.id, c.name]));
  const text = decodeMentions(decodeChannelRefs(markdownToPlain(source, { spoiler: SPOILER_MASK }), byId), names).replace(/\s+/g, " ").trim();
  const files = (m.body.trim() ? m.attachments : (m.attachments ?? m.forward?.attachments))?.length ?? 0;
  return (
    <div className="pasted-link" aria-label={`Link to ${who}'s message`}>
      <div className="msglink static">
        <span className="msglink-head">
          <Avatar id={m.authorId} name={who} size={16} />
          <strong>{who}</strong>
          <span className="msglink-where">{where}</span>
        </span>
        {text && <span className="msglink-text">{text}</span>}
        {files > 0 && <span className="msglink-files">{files} {files === 1 ? "attachment" : "attachments"}</span>}
      </div>
      <CloseButton size="sm" onClick={onRemove} aria-label="Remove link" title="Remove link" />
    </div>
  );
}
